import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Trash2, Calendar, X, Lock, Unlock } from 'lucide-react';
import AmountInput from '../common/AmountInput';
import AutocompleteInput from '../common/AutocompleteInput';
import Tooltip from '../common/Tooltip';
import CategoryIcon from '../common/CategoryIcon';
import ConfirmDialog from '../common/ConfirmDialog';
import SectionLabel from '../common/SectionLabel';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import useCategories from '../../hooks/useCategories';
import useToast from '../../hooks/useToast';

const formatGermanDate = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date)) return '';
  return new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

const parseDateToISO = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return null;
  return format(date, 'yyyy-MM-dd');
};

const INTERVAL_PRESETS = [
  { label: 'Monatlich', value: 1 },
  { label: 'Vierteljährlich', value: 3 },
];

const AddTransactionModal = ({ onClose, onSuccess, editData }) => {
  const { apiFetch } = useAuth();
  const { categories } = useCategories();
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState(0);
  const [isExpense, setIsExpense] = useState(true);
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [lockedSplitIndex, setLockedSplitIndex] = useState(0);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const [isRecurring, setIsRecurring] = useState(false);
  const [intervalVal, setIntervalVal] = useState(1);
  const [isCustomInterval, setIsCustomInterval] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [isStaged, setIsStaged] = useState(false);

  const isEffectivelyPlanned = useMemo(() => {
    if (!date) return true;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d > today;
  }, [date]);

  const isEditingPlanned = editData && (editData.is_booked === 0 || editData.is_booked === false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await apiFetch('/api/transactions/suggestions');
        if (response.ok) {
          const data = await response.json();
          setNameSuggestions(data);
        }
      } catch (err) {
        console.error('Failed to fetch suggestions', err);
      }
    };
    fetchSuggestions();
  }, [apiFetch]);

  useEffect(() => {
    if (editData) {
      setIsRecurring(editData.is_planned === 1 || editData.is_planned === true);
      const iv = editData.interval || 0;
      if (iv > 0) {
        const isPreset = INTERVAL_PRESETS.some(p => p.value === iv);
        setIsCustomInterval(!isPreset);
        setIntervalVal(iv);
      } else {
        setIntervalVal(1);
        setIsCustomInterval(false);
      }
      setEndDate(editData.end_date ? parseDateToISO(editData.end_date) : '');

      setName(editData.name || '');
      setIsExpense(editData.amount < 0);
      setAmount(Math.abs(editData.amount || 0));
      let isoDateStr = '';
      if (editData.date) {
        const d = new Date(editData.date);
        if (!isNaN(d)) isoDateStr = format(d, 'yyyy-MM-dd');
      }
      setDate(isoDateStr);
      setDescription(editData.description || '');
      setIsStaged(editData.is_reserved === 1 || editData.is_reserved === true);

      const formattedCategories = (editData.categories || []).map(cat => ({
        ...cat,
        split_amount: Math.abs(cat.split_amount || 0)
      }));
      setSelectedCategories(formattedCategories);
      setLockedSplitIndex(0);
    } else {
      setIsRecurring(false);
      setIntervalVal(1);
      setIsCustomInterval(false);
      setEndDate('');
      setDate('');
      setIsStaged(false);
      setLockedSplitIndex(0);
    }
  }, [editData]);

  useEffect(() => {
    if (!isEffectivelyPlanned) {
      setIsRecurring(false);
      setEndDate('');
    }
  }, [isEffectivelyPlanned]);

  const balanceLockedSplit = (list, lockedIndex, totalAmount) => {
    const newSelected = [...list];
    const sumOfOthers = newSelected.reduce(
      (s, c, i) => (i === lockedIndex ? s : s + (parseFloat(c.split_amount) || 0)),
      0
    );
    newSelected[lockedIndex] = {
      ...newSelected[lockedIndex],
      split_amount: Math.max(0, totalAmount - sumOfOthers)
    };
    return newSelected;
  };

  const updateSplits = (totalAmount) => {
    if (selectedCategories.length === 1) {
      setSelectedCategories([{ ...selectedCategories[0], split_amount: totalAmount }]);
    } else if (selectedCategories.length >= 2) {
      setSelectedCategories(balanceLockedSplit(selectedCategories, lockedSplitIndex, totalAmount));
    }
  };

  const toggleCategory = (cat) => {
    let newSelected;
    let newLockedIndex = lockedSplitIndex;

    const existingIndex = selectedCategories.findIndex(c => c.id === cat.id);
    if (existingIndex !== -1) {
      newSelected = selectedCategories.filter(c => c.id !== cat.id);
      if (existingIndex === lockedSplitIndex) {
        newLockedIndex = 0;
      } else if (existingIndex < lockedSplitIndex) {
        newLockedIndex = lockedSplitIndex - 1;
      }
      // existingIndex > lockedSplitIndex: locked item is unaffected, index unchanged
    } else {
      newSelected = [...selectedCategories, { ...cat, split_amount: 0 }];
    }

    if (newSelected.length === 1) {
      newSelected[0] = { ...newSelected[0], split_amount: amount };
      newLockedIndex = 0;
    } else if (newSelected.length >= 2) {
      newSelected = balanceLockedSplit(newSelected, newLockedIndex, amount);
    }

    setSelectedCategories(newSelected);
    setLockedSplitIndex(newLockedIndex);
  };

  const handleSplitChange = (index, val) => {
    if (index === lockedSplitIndex) return;
    const newSelected = [...selectedCategories];
    newSelected[index] = { ...newSelected[index], split_amount: val };
    setSelectedCategories(balanceLockedSplit(newSelected, lockedSplitIndex, amount));
  };

  const handleLockSplit = (index) => {
    if (index === lockedSplitIndex) return;
    setSelectedCategories(balanceLockedSplit(selectedCategories, index, amount));
    setLockedSplitIndex(index);
  };

  const reportError = (message) => {
    setError(message);
    showToast(message);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const response = await apiFetch(`/api/transactions/${editData.id}`, {
        method: 'DELETE'
      });
      if (response.ok) onSuccess();
      else reportError('Fehler beim Löschen des Eintrags');
    } catch {
      reportError('Verbindungsfehler');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalAmount = isExpense ? -amount : amount;

    if (!amount) {
      reportError('Bitte einen Betrag ungleich 0 € eingeben.');
      return;
    }

    if (selectedCategories.length > 1) {
      const sum = selectedCategories.reduce((s, c) => s + (parseFloat(c.split_amount) || 0), 0);
      if (Math.abs(sum - amount) > 0.01) {
        reportError(`Die Aufteilung (${sum.toFixed(2).replace('.', ',')} €) muss dem Gesamtbetrag (${amount.toFixed(2).replace('.', ',')} €) entsprechen.`);
        return;
      }
    }

    let formattedDate = null;
    if (date) {
      formattedDate = parseDateToISO(date);
      if (!formattedDate) { reportError('Das Datum ist ungültig.'); return; }
    }

    const url = editData ? `/api/transactions/${editData.id}` : `/api/transactions`;
    const method = editData ? 'PUT' : 'POST';

    const body = {
      name,
      amount: finalAmount,
      date: formattedDate,
      description,
      is_booked: !isEffectivelyPlanned,
      is_reserved: isStaged,
      is_planned: isRecurring,
      interval: isRecurring ? parseInt(intervalVal) || 1 : 0,
      end_date: endDate || null,
      categories: selectedCategories.map(c => ({
        ...c,
        split_amount: isExpense ? -c.split_amount : c.split_amount
      }))
    };

    try {
      const response = await apiFetch(url, {
        method,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        reportError(data.message || 'Fehler beim Speichern des Eintrags');
      }
    } catch {
      reportError('Verbindungsfehler');
    }
  };

  const setDateTo = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    setDate(format(d, 'yyyy-MM-dd'));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center md:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl w-full md:max-w-lg overflow-hidden border border-neutral-100 max-h-[92vh] md:max-h-[90vh] flex flex-col rounded-t-3xl md:rounded-3xl animate-sheet-up md:animate-none pb-[env(safe-area-inset-bottom)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 md:hidden flex-none">
          <div className="w-10 h-1 bg-neutral-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-none">
          <h2 className="text-lg font-bold text-neutral-800">
            {editData ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Expense / Income toggle */}
          <div className="flex bg-neutral-100 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setIsExpense(true)}
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                isExpense ? 'bg-white text-red-600 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              Ausgabe
            </button>
            <button
              type="button"
              onClick={() => setIsExpense(false)}
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                !isExpense ? 'bg-white text-emerald-600 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              Einkommen
            </button>
          </div>

          {/* Name input */}
          <div>
            <SectionLabel className="block mb-1.5">Name *</SectionLabel>
            <AutocompleteInput
              required
              value={name}
              onChange={setName}
              suggestions={nameSuggestions}
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm bg-neutral-50 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder:text-neutral-300"
              placeholder="Miete, Aldi, Döner..."
            />
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <SectionLabel className="block mb-1.5">Betrag *</SectionLabel>
              <AmountInput
                value={amount}
                onChange={(val) => { setAmount(val); updateSplits(val); }}
                className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm bg-neutral-50 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right font-mono transition-all"
              />
            </div>
            <div>
              <SectionLabel className="block mb-1.5">Datum</SectionLabel>
              <div
                className="relative w-full group cursor-pointer"
                onClick={() => document.getElementById('date-input').showPicker()}
              >
                <input
                  type="text"
                  readOnly
                  placeholder="TT. MMM JJJJ"
                  value={formatGermanDate(date)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm outline-none text-neutral-700 bg-neutral-50 group-hover:bg-neutral-100 transition-all cursor-pointer"
                />
                <span className="absolute top-1/2 right-3 transform -translate-y-1/2 text-neutral-400 group-hover:text-neutral-600 transition-colors">
                  <Calendar size={15} />
                </span>
                <input
                  id="date-input"
                  type="date"
                  value={date || ''}
                  onChange={(e) => setDate(e.target.value || '')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
              </div>
              {/* Quick date buttons */}
              <div className="flex justify-end gap-1 mt-1.5">
                {[
                  { days: 2, label: 'Vorgestern' },
                  { days: 1, label: 'Gestern' },
                  { days: 0, label: 'Heute' }
                ].map(({ days, label }) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setDateTo(days)}
                    className="flex-1 px-1.5 py-1 text-[10px] font-semibold text-neutral-500 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Planned / Staged toggles */}
          {(isEffectivelyPlanned || isEditingPlanned) && (
            <div className="space-y-3 pt-2">
              {/* Recurring */}
              <div className="bg-amber-50/50 border border-amber-100 rounded-2xl px-3 py-2 space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-neutral-300 flex-none"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-neutral-700">ist wiederkehrend</span>
                    <p className="text-[11px] text-neutral-400 mt-0.5 leading-tight">
                      Wird in den angegebenen Intervallen wiederholt eingetragen
                    </p>
                  </div>
                </label>

                {isRecurring && (
                  <div className="space-y-3 pt-1">
                    {/* Interval presets */}
                    <div>
                      <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1.5">
                        Intervall
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {INTERVAL_PRESETS.map(preset => (
                          <button
                            key={preset.value}
                            type="button"
                            onClick={() => { setIntervalVal(preset.value); setIsCustomInterval(false); }}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all ${
                              !isCustomInterval && parseInt(intervalVal) === preset.value
                                ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-amber-300 hover:text-amber-700'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => { setIsCustomInterval(true); setIntervalVal(''); }}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all ${
                            isCustomInterval
                              ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                              : 'bg-white text-neutral-600 border-neutral-200 hover:border-amber-300 hover:text-amber-700'
                          }`}
                        >
                          Benutzerdefiniert
                        </button>
                      </div>
                      {isCustomInterval && (
                        <div className="flex items-center gap-2 mt-2 bg-white px-3 py-1.5 rounded-xl border border-neutral-200 w-fit">
                          <span className="text-xs text-neutral-500">alle</span>
                          <input
                            type="number"
                            min="1"
                            value={intervalVal}
                            onChange={(e) => setIntervalVal(e.target.value)}
                            autoFocus
                            className="w-12 text-center text-sm font-semibold border-b border-neutral-300 focus:border-amber-500 outline-none"
                          />
                          <span className="text-xs text-neutral-500">Monate</span>
                        </div>
                      )}
                    </div>

                    {/* End date */}
                    <div>
                      <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1.5">
                        Enddatum <span className="font-normal normal-case text-neutral-400">(optional)</span>
                      </label>
                      <div
                        className="relative w-full group cursor-pointer"
                        onClick={() => document.getElementById('end-date-input').showPicker()}
                      >
                        <input
                          type="text"
                          readOnly
                          placeholder="Kein Enddatum"
                          value={formatGermanDate(endDate)}
                          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-xl outline-none text-neutral-700 bg-white"
                        />
                        <span className="absolute top-1/2 right-3 transform -translate-y-1/2 text-neutral-400">
                          <Calendar size={14} />
                        </span>
                        <input
                          id="end-date-input"
                          type="date"
                          value={endDate || ''}
                          onChange={(e) => setEndDate(e.target.value || '')}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                      </div>
                      {endDate && (
                        <button
                          type="button"
                          onClick={() => setEndDate('')}
                          className="text-[10px] text-red-500 hover:text-red-700 mt-1 font-semibold flex items-center gap-1"
                        >
                          Enddatum entfernen
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Staged */}
              <label className="flex items-center gap-2.5 px-3 py-2 bg-blue-50/50 border border-blue-100 rounded-2xl cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isStaged}
                  onChange={(e) => setIsStaged(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-neutral-300 flex-none mt-0.5"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-neutral-700">Ist vorgemerkt</span>
                  <p className="text-[11px] text-neutral-400 mt-0.5 leading-tight">
                    Wird direkt auf den aktuellen Kontostand angerechnet
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Categories */}
          <div>
            <SectionLabel className="block mb-2">Kategorien</SectionLabel>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {categories.map(cat => {
                const isSelected = selectedCategories.find(c => c.id === cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                      isSelected
                        ? isExpense
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                    }`}
                  >
                    <CategoryIcon
                      name={cat.icon_name}
                      size={14}
                      className={isSelected ? (isExpense ? 'text-red-600' : 'text-emerald-600') : 'text-neutral-500'}
                    />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            {selectedCategories.length > 1 && (
              <div className="space-y-2 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">Beträge aufteilen</p>
                {selectedCategories.map((cat, index) => {
                  const isLocked = index === lockedSplitIndex;
                  return (
                    <div key={cat.id} className="flex justify-between items-center gap-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Tooltip content={isLocked ? 'Wird automatisch berechnet' : 'Diesen Betrag automatisch berechnen'} position="top">
                          <button
                            type="button"
                            onClick={() => handleLockSplit(index)}
                            className={`p-1 rounded-md transition-all flex-shrink-0 ${
                              isLocked
                                ? 'text-primary-600'
                                : 'text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100'
                            }`}
                          >
                            {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                          </button>
                        </Tooltip>
                        <span className="text-xs font-medium text-neutral-600 truncate">{cat.name}</span>
                      </div>
                      <div className="flex items-center">
                        <AmountInput
                          value={cat.split_amount}
                          onChange={(val) => handleSplitChange(index, val)}
                          className={`w-24 px-2 py-1 text-xs border rounded-lg text-right font-mono outline-none transition-all ${
                            isLocked
                              ? 'border-neutral-200 bg-neutral-100 text-neutral-400 cursor-not-allowed'
                              : 'border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                          }`}
                          disabled={isLocked}
                        />
                        <span className="ml-1.5 text-xs text-neutral-400 font-medium">€</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <SectionLabel className="block mb-1.5">Beschreibung</SectionLabel>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Notiz, Belegdetails, etc."
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm bg-neutral-50 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder:text-neutral-300"
              rows="2"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className="pt-2 flex gap-3 border-t border-neutral-100">
            {editData && (
              <Tooltip content="Löschen" position="top">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2.5 border border-red-200 text-red-500 rounded-xl hover:bg-red-50 transition-all flex items-center justify-center"
                >
                  <Trash2 size={20} />
                </button>
              </Tooltip>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-600 rounded-xl hover:bg-neutral-50 transition-all font-semibold text-sm"
            >
              Abbrechen
            </button>
            <Button
              type="submit"
              className="flex-1 px-4 py-2.5 text-sm"
            >
              Speichern
            </Button>
          </div>
        </form>
      </div>

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Eintrag löschen?"
          message="Soll dieser Eintrag wirklich gelöscht werden? Das kann nicht rückgängig gemacht werden."
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default AddTransactionModal;
